import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCashTips, CashTip, updateCashTipStatus } from '../services/cashTipService';
import { getJarTips, JarTip, updateJarTipStatus } from '../services/jarTipService';
import { getDisputes, Dispute } from '../services/disputeService';
import { rejectTransaction } from '../services/approvalService';
import { getStaff, Staff } from '../services/staffService';
import { useFirebase } from '../contexts/FirebaseContext';
import { AlertCircle, CheckCircle, Loader2, Users, DollarSign, XCircle } from 'lucide-react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { getSettings } from '../services/settingsService';
import { formatCurrency } from '../lib/formatters';
import { UnifiedTip } from '../services/allocationService';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo = {
    error: error instanceof Error ? error.message : String(error),
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

interface Props {
  tenantId: string;
}

export const ReviewApproveTips: React.FC<Props> = ({ tenantId }) => {
  const [tips, setTips] = useState<UnifiedTip[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [overrides, setOverrides] = useState<Record<string, 'Paid' | 'Unpaid'>>({});

  const [needsMethod, setNeedsMethod] = useState(false);

  useEffect(() => {
    if (!tenantId) return;

    // Load staff roster for identifier lookups
    getStaff(tenantId).then(staff => {
      setStaffList(staff);
    }).catch(err => console.error('Error fetching staff list:', err));

    // Check for allocation method
    getSettings(tenantId).then(settings => {
      if (!settings || !settings.allocationMethod) {
        setNeedsMethod(true);
      }
    });

    setLoading(true);

    let cashLoaded = false;
    let jarLoaded = false;
    let posLoaded = false;
    let disputesLoaded = false;

    const checkAllLoaded = () => {
      if (cashLoaded && jarLoaded && posLoaded && disputesLoaded) {
        setLoading(false);
      }
    };
    
    // Listen to Cash Tips
    const cashTipsRef = collection(db, 'tenants', tenantId, 'cash_tips');
    const cashTipsQuery = query(cashTipsRef, where('status', '==', 'Pending'));
    const unsubscribeCash = onSnapshot(cashTipsQuery, (snapshot) => {
      const cashTips = snapshot.docs.map(doc => ({
        id: doc.id,
        staffName: doc.data().staffName,
        staffId: doc.data().staffId,
        posId: doc.data().posId,
        email: doc.data().email,
        shift: doc.data().shiftName || doc.data().shift,
        tipDate: doc.data().tipDate,
        amount: doc.data().amount,
        status: doc.data().status,
        source: 'Cash' as const,
        source_id: doc.id,
        type: 'Submitted' as const,
        paymentStatus: doc.data().paymentStatus,
        submittedBy: doc.data().submittedBy,
        masterTxId: doc.data().masterTxId
      }));
      setTips(prev => {
        const otherTips = prev.filter(t => t.source !== 'Cash');
        return [...otherTips, ...cashTips];
      });
      cashLoaded = true;
      checkAllLoaded();
    }, (error) => {
      console.error('Error fetching cash tips:', error);
      cashLoaded = true;
      checkAllLoaded();
    });

    // Listen to Jar Tips
    const jarTipsRef = collection(db, 'tenants', tenantId, 'jar_tips');
    const jarTipsQuery = query(jarTipsRef, where('status', '==', 'Pending'));
    const unsubscribeJar = onSnapshot(jarTipsQuery, (snapshot) => {
      const jarTips = snapshot.docs.map(doc => ({
        id: doc.id,
        staffName: doc.data().submitterName,
        staffId: doc.data().staffId,
        posId: doc.data().posId,
        email: doc.data().email,
        shift: doc.data().shift,
        tipDate: doc.data().tipDate,
        amount: doc.data().jarAmount,
        status: doc.data().status,
        source: 'Jar' as const,
        source_id: doc.id,
        type: 'Submitted' as const,
        paymentStatus: doc.data().paymentStatus,
        masterTxId: doc.data().masterTxId
      }));
      setTips(prev => {
        const otherTips = prev.filter(t => t.source !== 'Jar');
        return [...otherTips, ...jarTips];
      });
      jarLoaded = true;
      checkAllLoaded();
    }, (error) => {
      console.error('Error fetching jar tips:', error);
      jarLoaded = true;
      checkAllLoaded();
    });

    // Listen to POS Tips
    const posTipsRef = collection(db, 'tenants', tenantId, 'master_transactions');
    const posTipsQuery = query(posTipsRef, where('source', 'in', ['POS', 'POS Override']), where('submissionStatus', '==', 'Pending'));
    const unsubscribePos = onSnapshot(posTipsQuery, (snapshot) => {
      const allPosTips = snapshot.docs.map(doc => ({
        id: doc.id,
        staffName: doc.data().staffName,
        staffId: doc.data().staffId,
        posId: doc.data().posId,
        shift: doc.data().shift,
        email: doc.data().email,
        tipDate: doc.data().tipDate,
        amount: doc.data().amount,
        status: doc.data().submissionStatus,
        source: 'POS' as const,
        source_id: doc.id,
        type: 'Submitted' as const,
        masterTxId: doc.id,
        payrollIncluded: doc.data().payrollIncluded || false, batchId: doc.data().batchId
      }));

      // Deduplicate and filter: show all unique pending tips across all active batches
      const uniquePosTips = Array.from(new Map(allPosTips
        .filter(t => !t.payrollIncluded)
        .map(item => [item.masterTxId, item])).values());

      setTips(prev => {
        const otherTips = prev.filter(t => t.source !== 'POS');
        return [...otherTips, ...uniquePosTips];
      });
      posLoaded = true;
      checkAllLoaded();
    }, (error) => {
      console.error('Error fetching POS tips:', error);
      posLoaded = true;
      checkAllLoaded();
    });

    // Listen to Disputes
    const disputesRef = collection(db, 'tenants', tenantId, 'disputes');
    const disputesQuery = query(disputesRef, where('status', '==', 'Pending'));
    const unsubscribeDisputes = onSnapshot(disputesQuery, (snapshot) => {
      setDisputes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Dispute)));
      disputesLoaded = true;
      checkAllLoaded();
    }, (error) => {
      console.error('Error fetching disputes:', error);
      disputesLoaded = true;
      checkAllLoaded();
    });

    return () => {
      unsubscribeCash();
      unsubscribeJar();
      unsubscribePos();
      unsubscribeDisputes();
    };
  }, [tenantId]);

  const { user } = useFirebase();
  const [isApproving, setIsApproving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [rejectingTip, setRejectingTip] = useState<UnifiedTip | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  const pendingTips = tips.filter(tip => !disputes.some(d => d.email === tip.email && d.tipDate === tip.tipDate && d.status === 'Pending'));
  const pendingAmount = pendingTips.reduce((acc, tip) => acc + tip.amount, 0);

  const handleApproveAll = async () => {
    if (!user || tips.length === 0) return;

    // Filter out tips that have pending disputes to avoid accidental incorrect approvals
    const tipsToApprove = tips.filter(tip => 
      !disputes.some(d => 
        d.email === tip.email && 
        d.tipDate === tip.tipDate && 
        d.shift === (tip as any).shift &&
        d.status === 'Pending'
      )
    );

    if (tipsToApprove.length === 0) {
      alert('All pending tips have associated disputes. Please resolve the disputes first.');
      return;
    }

    // Check if allocation method is set
    const settings = await getSettings(tenantId);
    if (!settings || !settings.allocationMethod) {
      alert('Action Required: Please set your Tip Allocation Method in Settings before approving tips.');
      return;
    }
    
    setIsApproving(true);
    setSuccessMessage('');

    try {
      const promises = tipsToApprove.map(tip => {
        if (!tip.id) return Promise.resolve();
        return handleSingleApprove(tip);
      });

      await Promise.all(promises);
      setSuccessMessage(`Successfully approved ${tipsToApprove.length} tips!`);
    } catch (error) {
      console.error('Error approving tips in ReviewApproveTips:', error);
      alert('Failed to approve some tips. Check the error log.');
    } finally {
      setIsApproving(false);
    }
  };

  const handleSingleApprove = async (tip: UnifiedTip) => {
    if (!user || !tip.id) return;
    
    const settings = await getSettings(tenantId);
    if (!settings || !settings.allocationMethod) {
      alert('Action Required: Please set your Tip Allocation Method in Settings before approving tips.');
      return;
    }

    if (tip.source === 'Cash') {
      await updateCashTipStatus(tenantId, tip.id!, tip.masterTxId || 'unknown', {
        status: 'Approved',
        approverId: user.email || 'unknown',
        allocatedAmount: tip.amount
      });
    } else if (tip.source === 'Jar') {
      await updateJarTipStatus(tenantId, tip.id!, tip.masterTxId || 'unknown', {
        status: 'Approved',
        approverId: user.email || 'unknown',
        allocatedAmount: tip.amount,
        paymentStatus: overrides[tip.id!] || tip.paymentStatus
      });
    } else if (tip.source === 'POS') {
      // Approve POS tip: update master transaction status
      const { doc, updateDoc } = await import('firebase/firestore');
      const txRef = doc(db, 'tenants', tenantId, 'master_transactions', tip.id!);
      const { serverTimestamp } = await import('firebase/firestore');
      await updateDoc(txRef, {
        submissionStatus: 'Approved',
        allocationStatus: 'Finalized',
        reportingStatus: 'pos_allocated',
        auditVerified: true,
        auditActor: user.email,
        auditTimestamp: serverTimestamp()
      });
    }
  };

  const handleReject = (tip: UnifiedTip) => {
    setRejectingTip(tip);
  };

  const confirmReject = async () => {
    if (!rejectingTip || !rejectingTip.id || !rejectionReason.trim()) return;

    try {
      const collectionName = rejectingTip.source === 'Cash' ? 'cash_tips' : rejectingTip.source === 'Jar' ? 'jar_tips' : 'master_transactions';
      await rejectTransaction(tenantId, collectionName, rejectingTip.id, rejectionReason);
      setRejectingTip(null);
      setRejectionReason('');
    } catch (error) {
      console.error('Error rejecting tip:', error);
      alert('Failed to reject tip.');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="card p-6 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Review & Approve</h2>
          <p className="text-gray-500 mt-1">Verify submitted tips and resolve disputes.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card p-6 bg-white border border-gray-100">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
              <DollarSign size={20} />
            </div>
            <h3 className="font-bold text-gray-900">Total Pending Amount</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">{formatCurrency(pendingAmount)}</p>
        </div>

        <div className="card p-6 bg-white border border-gray-100">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
              <Users size={20} />
            </div>
            <h3 className="font-bold text-gray-900">Pending Tips Count</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">{pendingTips.length}</p>
        </div>
      </div>
      
      
       <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <p className="text-xs text-amber-600 font-medium bg-amber-50 px-3 py-1 rounded-lg inline-block border border-amber-100">
          Note: POS tips are imported pre-allocated and are not managed by this system.
        </p>
        <button 
          onClick={handleApproveAll}
          disabled={tips.length === 0 || isApproving}
          className="btn-primary !bg-green-600 hover:!bg-green-700 shadow-lg shadow-green-500/20 disabled:bg-gray-200 disabled:text-gray-400 flex items-center gap-2"
        >
          {isApproving ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              Approving...
            </>
          ) : (
            `Approve All (${pendingTips.length})`
          )}
        </button>
      </div>

      {needsMethod && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3 text-amber-800 animate-in fade-in slide-in-from-top-2">
          <AlertCircle size={20} className="text-amber-500 shrink-0" />
          <p className="text-sm font-medium">
            <span className="font-bold">Setup Required:</span> Please select an allocation method in <Link to="/settings" className="underline font-bold">Settings</Link> to enable approvals.
          </p>
        </div>
      )}

      {successMessage && (
        <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start space-x-3 animate-in fade-in slide-in-from-top-2">
          <CheckCircle className="text-green-600 mt-0.5" size={20} />
          <div>
            <h3 className="text-sm font-bold text-green-800">Success</h3>
            <p className="text-sm text-green-700 mt-1">{successMessage}</p>
          </div>
        </div>
      )}

      {disputes.length > 0 && (
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start space-x-3">
          <AlertCircle className="text-amber-600 mt-0.5" size={20} />
          <div>
            <h3 className="text-sm font-bold text-amber-800">Action Required</h3>
            <p className="text-sm text-amber-700 mt-1">There are {disputes.length} unresolved disputes. Please resolve them before approving tips.</p>
          </div>
        </div>
      )}

      {/* Mobile View: Cards */}
      <div className="md:hidden space-y-4">
        {tips.length > 0 ? tips.map((tip, index) => {
          const hasDispute = disputes.some(d => d.email === tip.email && d.tipDate === tip.tipDate && d.status === 'Pending');
          const matchedStaff = staffList.find(s => 
            (tip.staffId && s.id === tip.staffId) || 
            (s.email && tip.email && s.email.toLowerCase() === tip.email.toLowerCase()) ||
            (s.name && tip.staffName && s.name.toLowerCase() === tip.staffName.toLowerCase())
          );
          const resolvedPosId = tip.posId || matchedStaff?.posId;
          const resolvedStaffId = tip.staffId || matchedStaff?.id;
          const displayName = tip.staffName || matchedStaff?.name || tip.email || 'Unknown Staff';

          return (
            <div key={tip.id || index} className={`p-4 rounded-xl border space-y-3 ${hasDispute ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}>
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <p className="font-bold text-gray-900 text-base">{displayName}</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {resolvedPosId && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-mono font-bold uppercase">
                        POS ID: {resolvedPosId}
                      </span>
                    )}
                    {resolvedStaffId && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 text-[10px] font-mono font-bold" title={`Full Staff ID: ${resolvedStaffId}`}>
                        Staff ID: {resolvedStaffId.slice(0, 8)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{tip.tipDate} {tip.shift && `• ${tip.shift}`}</p>
                  {tip.email && <p className="text-[11px] text-gray-400 font-medium">Email: {tip.email}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                    tip.source === 'Cash' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                  }`}>
                    {tip.source}
                  </span>
                  {hasDispute && (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
                      <AlertCircle size={10} />
                      Disputed
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-600">Amount</span>
                <span className="text-lg font-bold text-gray-900">{formatCurrency(tip.amount)}</span>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => handleSingleApprove(tip)}
                  disabled={hasDispute}
                  className={`w-full py-2 rounded-lg text-xs font-bold transition-all ${
                    hasDispute 
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                >
                  {hasDispute ? 'Resolve Dispute First' : 'Approve'}
                </button>
                <button 
                  onClick={() => handleReject(tip)}
                  disabled={hasDispute}
                  className={`w-full py-2 rounded-lg text-xs font-bold transition-all ${
                    hasDispute 
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  Reject
                </button>
              </div>
            </div>
          );
        }) : (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <p className="text-gray-400 font-medium">No pending tips to review</p>
          </div>
        )}
      </div>

      {/* Desktop View: Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Staff Member & IDs</th>
              <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Date & Shift</th>
              <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-widest text-right">Amount</th>
              <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-widest text-center">Type</th>
              <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-widest text-center">Payment Status</th>
              <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-widest text-right">Status</th>
              <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {tips.length > 0 ? tips.map((tip, index) => {
              const hasDispute = disputes.some(d => d.email === tip.email && d.tipDate === tip.tipDate && d.status === 'Pending');
              const matchedStaff = staffList.find(s => 
                (tip.staffId && s.id === tip.staffId) || 
                (s.email && tip.email && s.email.toLowerCase() === tip.email.toLowerCase()) ||
                (s.name && tip.staffName && s.name.toLowerCase() === tip.staffName.toLowerCase())
              );
              const resolvedPosId = tip.posId || matchedStaff?.posId;
              const resolvedStaffId = tip.staffId || matchedStaff?.id;
              const displayName = tip.staffName || matchedStaff?.name || tip.email || 'Unknown Staff';

              return (
                <tr key={tip.id || index} className={`group transition-colors ${hasDispute ? 'bg-amber-50/50 hover:bg-amber-100/50' : 'hover:bg-gray-50/50'}`}>
                  <td className="py-4 font-semibold text-gray-900">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900 text-sm">{displayName}</span>
                        {hasDispute && (
                          <div className="group relative">
                            <AlertCircle size={14} className="text-amber-500" />
                            <div className="absolute left-full ml-2 top-0 bg-gray-900 text-white text-[10px] p-2 rounded w-48 invisible group-hover:visible z-10">
                              {(() => {
                                const dispute = disputes.find(d => d.email === tip.email && d.tipDate === tip.tipDate && d.status === 'Pending');
                                return dispute ? <>Original: {formatCurrency(dispute.originalAmount)}<br/>Reason: {dispute.reason}</> : 'Unresolved dispute.';
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {resolvedPosId && (
                          <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-blue-100 text-blue-800 text-[9px] font-mono font-bold uppercase">
                            POS ID: {resolvedPosId}
                          </span>
                        )}
                        {resolvedStaffId && (
                          <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-slate-200 text-slate-700 text-[9px] font-mono font-bold" title={`Full Staff ID: ${resolvedStaffId}`}>
                            Staff ID: {resolvedStaffId.slice(0, 8)}
                          </span>
                        )}
                        {tip.email && (
                          <span className="text-[10px] text-gray-400 font-medium">
                            {tip.email}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 text-gray-500">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-700">{tip.tipDate}</span>
                      {tip.shift && <span className="text-[10px] text-gray-400 font-medium">{tip.shift}</span>}
                    </div>
                  </td>
                  <td className="py-4 text-right font-bold text-gray-900">{formatCurrency(tip.amount)}</td>
                  <td className="py-4 text-center">
                    <span className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                      tip.source === 'Cash' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {tip.source}
                    </span>
                  </td>
                  <td className="py-4 text-center">
                    {(tip.source === 'Cash' || tip.source === 'Jar') ? (
                      <select 
                        value={overrides[tip.id!] || tip.paymentStatus}
                        onChange={(e) => setOverrides({...overrides, [tip.id!]: e.target.value as 'Paid' | 'Unpaid'})}
                        className="text-xs font-bold bg-transparent border-none focus:ring-0 cursor-pointer"
                      >
                        <option value="Paid">Paid</option>
                        <option value="Unpaid">Unpaid</option>
                      </select>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-4 text-right">
                    <span className={`text-xs font-medium px-2 py-1 rounded-lg border ${
                      hasDispute ? 'text-amber-600 bg-amber-50 border-amber-100' : 'text-blue-600 bg-blue-50 border-blue-100'
                    }`}>
                      {hasDispute ? 'Disputed' : 'Pending'}
                    </span>
                  </td>
                  <td className="py-4 text-right flex items-center justify-end gap-2">
                    <button 
                  onClick={() => handleSingleApprove(tip)}
                  disabled={isApproving || hasDispute}
                  className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded border transition-all ${
                    hasDispute 
                      ? 'text-gray-300 border-gray-200 cursor-not-allowed' 
                      : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'
                  }`}
                >
                  Approve
                </button>
                <button 
                  onClick={() => handleReject(tip)}
                  disabled={isApproving || hasDispute}
                  className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded border transition-all ${
                    hasDispute 
                      ? 'text-gray-300 border-gray-200 cursor-not-allowed' 
                      : 'text-red-600 border-red-200 hover:bg-red-50'
                  }`}
                >
                  Reject
                </button>
              </td>
            </tr>
          );
        }) : (
          <tr>
            <td colSpan={7} className="py-20 text-center text-gray-400 font-medium">
              No pending tips to review
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
      {/* Rejection Modal */}
      {rejectingTip && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Reject Tip</h3>
            <p className="text-sm text-gray-500 mb-4">Please enter a reason for rejecting tip for <span className="font-bold">{rejectingTip.staffName}</span> ({formatCurrency(rejectingTip.amount)})</p>
            <textarea
              className="w-full p-3 border border-gray-200 rounded-lg text-sm mb-4"
              rows={3}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Reason for rejection..."
            />
            <div className="flex gap-2">
              <button 
                onClick={() => setRejectingTip(null)}
                className="flex-1 py-2 text-sm font-bold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button 
                onClick={confirmReject}
                disabled={!rejectionReason.trim()}
                className="flex-1 py-2 text-sm font-bold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-red-300"
              >
                Reject Tip
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
